import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { appContext } from 'common/contexts/appContext';
import { ClosedOrder, Order, OrderStatuses, CashRegisterState, CashMovement, CashMovementType } from 'common/types';
import { getTimestamp } from 'common/utils';
import { db, auth } from '../../../firebase/config';
import { collection, query, where, getDocs, Timestamp, doc, orderBy, limit, updateDoc } from 'firebase/firestore';
import { setDocWithCreatedBy } from '../../../firebase/utils';
import styles from './cashierCloseSimple.module.css';

type CashierCloseSimpleProps = {
  isOpen: boolean;
  onClose: () => void;
};

type CashierSummary = {
  totalSales: number;
  cashSales: number;
  cardSales: number;
  orderCount: number;
};

type CashierCloseData = {
  date: number;
  summary: CashierSummary;
  countedCash: number;
  difference: number;
  userId: string | null;
  initialAmount: number;
  cashMovements: {
    additions: number;
    withdrawals: number;
  };
  expectedCash: number;
};

const CashierCloseSimple: React.FC<CashierCloseSimpleProps> = ({ isOpen, onClose }) => {
  const [t] = useTranslation();
  const { state } = useContext(appContext);
  const [summary, setSummary] = useState<CashierSummary>({
    totalSales: 0,
    cashSales: 0,
    cardSales: 0,
    orderCount: 0,
  });
  const [countedCash, setCountedCash] = useState<string>('');
  const [difference, setDifference] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [activeRegister, setActiveRegister] = useState<CashRegisterState | null>(null);
  const [expectedCash, setExpectedCash] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchSalesData();
      fetchCashRegisterState();
    }
  }, [isOpen]);

  // Calculate difference when countedCash changes
  useEffect(() => {
    const cashValue = parseFloat(countedCash) || 0;
    setDifference(cashValue - expectedCash);
  }, [countedCash, expectedCash]);

  // Calculate expected cash whenever activeRegister or summary changes
  useEffect(() => {
    if (activeRegister && summary) {
      // Calcular el efectivo esperado: monto inicial + ventas en efectivo
      let expected = activeRegister.initialAmount + summary.cashSales;

      // Obtener movimientos de caja desde Firestore
      fetchCashMovements(activeRegister.id).then(movements => {
        if (movements && movements.length > 0) {
          const additions = movements
            .filter(movement => movement.type === CashMovementType.ADDITION)
            .reduce((sum: number, movement: CashMovement) => sum + movement.amount, 0);

          const withdrawals = movements
            .filter(movement => movement.type === CashMovementType.WITHDRAWAL)
            .reduce((sum: number, movement: CashMovement) => sum + movement.amount, 0);

          expected += additions - withdrawals;
        }

        setExpectedCash(expected);
      });
    }
  }, [activeRegister, summary]);

  // Función para obtener los movimientos de caja
  const fetchCashMovements = async (registerId: string) => {
    try {
      const movementsCollection = collection(db, 'cashMovements');
      const q = query(
        movementsCollection,
        where('parentUserId', '==', state.parentId),
        where('registerId', '==', registerId),

      );

      const querySnapshot = await getDocs(q);
      const movements: CashMovement[] = [];

      querySnapshot.forEach((doc) => {
        movements.push({ id: doc.id, ...doc.data() } as CashMovement);
      });

      return movements;
    } catch (error) {
      console.error('Error fetching cash movements:', error);
      return [];
    }
  };

  const fetchCashRegisterState = async () => {
    setLoading(true);
    try {
      const cashRegisterCollection = collection(db, 'cashRegister');
      const q = query(
        cashRegisterCollection,
        where('parentUserId', '==', state.parentId),
        where('isOpen', '==', true),
        orderBy('openDate', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const registerData = querySnapshot.docs[0].data() as CashRegisterState;
        registerData.id = querySnapshot.docs[0].id;
        setActiveRegister(registerData);
      } else {
        setError(t('terminal.cashierClose.noActiveRegister', 'No hay caja abierta para cerrar'));
      }
    } catch (err) {
      console.error('Error fetching cash register state:', err);
      setError(t('terminal.cashierClose.errorFetching', 'Error al obtener el estado de la caja'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesData = async () => {
    setLoading(true);
    try {
      // Get today's start timestamp (midnight)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startOfDay = today.getTime();

      // Query closed orders from today
      const closedOrdersCollection = collection(db, 'closedOrders');
      const q = query(
        closedOrdersCollection,
        where('parentUserId', '==', state.parentId),
        where('dateClose', '>=', startOfDay)
      );

      const querySnapshot = await getDocs(q);

      // Calculate summary
      let totalSales = 0;
      let cashSales = 0;
      let cardSales = 0;
      let orderCount = 0;

      querySnapshot.forEach((doc) => {
        const order = doc.data() as ClosedOrder;
        totalSales += order.totalAmount;
        cashSales += order.cashPaymentAmount;
        cardSales += order.cardPaymentAmount;
        orderCount++;
      });

      const summaryData = {
        totalSales,
        cashSales,
        cardSales,
        orderCount,
      };

      setSummary(summaryData);

      // If we already have the register data, update the expected cash
      if (activeRegister) {
        const expected = activeRegister.initialAmount + cashSales;
        setExpectedCash(expected);
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
      setError(t('terminal.sales.errorFetchingData', 'Error al obtener datos de ventas'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!activeRegister) {
      console.error('No active register found');
      setError(t('terminal.cashRegister.noActiveRegister', 'No hay una caja abierta para cerrar'));
      return;
    }

    setSubmitting(true);
    try {
      const cashierCloseData: CashierCloseData = {
        date: getTimestamp(),
        summary,
        countedCash: parseFloat(countedCash) || 0,
        difference,
        userId: auth.currentUser?.uid || null,
        initialAmount: activeRegister.initialAmount,
        cashMovements: {
          additions: 0,
          withdrawals: 0
        },
        expectedCash
      };

      const optimistFunction = async () => {

        // Save to Firestore using setDocWithCreatedBy
        const cashierClosesCollection = collection(db, 'cashierCloses');
        await setDocWithCreatedBy(cashierClosesCollection, cashierCloseData);

        // Close the cash register
        const registerRef = doc(db, 'cashRegister', activeRegister.id);
        await updateDoc(registerRef, {
          isOpen: false,
          closeDate: getTimestamp()
        });

        // Create closing cash movement
        const cashMovementsCollection = collection(db, 'cashMovements');
        const movement = {
          type: CashMovementType.CLOSING,
          amount: parseFloat(countedCash) || 0,
          date: getTimestamp(),
          notes: t('terminal.cashierClose.notes', 'Cierre de caja'),
          userId: auth.currentUser?.uid || null,
        };

        await setDocWithCreatedBy(cashMovementsCollection, movement);
      };

      optimistFunction()
      // Close the dialog
      onClose();
    } catch (error) {
      console.error('Error submitting cashier close:', error);
      setError(t('terminal.cashierClose.errorSubmitting', 'Error al cerrar la caja'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <h2>{t('terminal.cashierClose.menuLabel', 'Cierre de Caja')}</h2>
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>{t('common.loading', 'Cargando...')}</div>
          ) : !activeRegister ? (
            <div className={styles.message}>
              {t('terminal.cashRegister.noActiveRegister', 'No hay una caja abierta para cerrar')}
            </div>
          ) : error ? (
            <div className={styles.error}>
              {error}
            </div>
          ) : (
            <>
              <div className={styles.registerInfo}>
                <h3>{t('terminal.cashRegister.info', 'Información de Caja')}</h3>
                <div className={styles.summaryItem}>
                  <span>{t('terminal.cashRegister.openedAt', 'Abierta el')}:</span>
                  <span>{formatDate(activeRegister.openDate)}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span>{t('terminal.cashRegister.initialAmount', 'Monto Inicial')}:</span>
                  <span className={styles.amount}>${activeRegister.initialAmount.toFixed(2)}</span>
                </div>
              </div>

              <div className={styles.summary}>
                <h3>{t('terminal.cashierClose.summary', 'Resumen de Ventas')}</h3>

                <div className={styles.summaryItem}>
                  <span>{t('terminal.cashierClose.totalSales', 'Ventas Totales')}:</span>
                  <span className={styles.amount}>${summary.totalSales.toFixed(2)}</span>
                </div>

                <div className={styles.summaryItem}>
                  <span>{t('terminal.cashierClose.cashSales', 'Ventas en Efectivo')}:</span>
                  <span className={styles.amount}>${summary.cashSales.toFixed(2)}</span>
                </div>

                <div className={styles.summaryItem}>
                  <span>{t('terminal.cashierClose.cardSales', 'Ventas con Tarjeta')}:</span>
                  <span className={styles.amount}>${summary.cardSales.toFixed(2)}</span>
                </div>

                <div className={styles.summaryItem}>
                  <span>{t('terminal.cashierClose.orderCount', 'Cantidad de Órdenes')}:</span>
                  <span>{summary.orderCount}</span>
                </div>
              </div>

              <div className={styles.cashCount}>
                <h3>{t('terminal.cashierClose.cashCount', 'Conteo de Efectivo')}</h3>

                <div className={styles.inputGroup}>
                  <label htmlFor="countedCash">
                    {t('terminal.cashierClose.countedCash', 'Efectivo Contado')}:
                  </label>
                  <input
                    id="countedCash"
                    type="number"
                    value={countedCash}
                    onChange={(e) => setCountedCash(e.target.value)}
                    placeholder="0.00"
                    className={styles.input}
                  />
                </div>

                <div className={styles.differenceContainer}>
                  <div className={styles.summaryItem}>
                    <span>{t('terminal.cashierClose.expected', 'Efectivo Esperado')}:</span>
                    <span className={styles.amount}>${expectedCash.toFixed(2)}</span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span>{t('terminal.cashierClose.difference', 'Diferencia')}:</span>
                    <span className={`${styles.amount} ${difference < 0 ? styles.negative : difference > 0 ? styles.positive : ''}`}>
                      ${difference.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.actions}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          {activeRegister && (
            <button
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={loading || submitting}
            >
              {submitting
                ? t('common.submitting', 'Enviando...')
                : t('terminal.cashierClose.submit', 'Confirmar Cierre')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashierCloseSimple;
